import React from "react";
import { ReactTFC } from "../../utils/fp/missing-ts-parts";
import { SwagSlugBuilder, getIsoCodeWithIntFallback } from "@swo/shared";
import { ExtractSwarovskiPageData, NewsletterLandingPageData } from "@swo/storefront";
import { PageMetadataComponent } from "../../components/PageMetadataComponent/PageMetadataComponent";
import { HeaderComponent } from "../../components/HeaderComponent/HeaderComponent";
import { HeaderSpacer } from "../_layout-helpers/spacers";
import { Spacer } from "../../components/_shared-components/arrangement/Space";

import { Navigate, useLocation } from "react-router";
import { NewsletterLandingPageComponent } from "./component/newsletter-landing-page-component";
import { useStore } from "../../store/hooks/use-store";
import { MAIL_LINK_FRAGMENT } from "./mail-link-fragment";
import { t2 } from "../../domain/translations/translations";
import { T } from "../../components/T";

export const NewsletterLandingPageLayout: ReactTFC<ExtractSwarovskiPageData<NewsletterLandingPageData>> = props => {
  const location = useLocation();
  const { locationOrigin, config } = useStore().state;
  const isNewsletterShown = config.newsletter.isNewsletterShown;
  const gt = t2(props.generalTranslations);

  const linkToHomepage = SwagSlugBuilder.path(props.locale, props.segment, "");
  const maybeInitialEmailValue = getInitialEmailValue(location.hash);

  function getInitialEmailValue(browserHash: string): string | undefined {
    if (browserHash.startsWith(MAIL_LINK_FRAGMENT)) {
      return browserHash.slice(MAIL_LINK_FRAGMENT.length);
    } else {
      return undefined;
    }
  }

  const termsAndConditions = {
    link: props.data.termsAndConditionsLink,
    label: props.data.translations["newsletter_landing_page.termsAndConditionsLabel"],
  };

  const otherConditions = {
    link: props.data.privacyPolicyLink,
    label: props.data.translations.otherConditionsLabel,
  };

  const translations = {
    iAccept: props.data.translations["newsletter_landing_page.iAccept"],
    and: props.data.translations["newsletter_landing_page.and"],
  };

  const { signUpTermsParagraph, paragraph, segmentSelectorHeadline, buttonLabel } = props.data.translations;
  const headline = props.data.translations["newsletter_landing_page.headline"];

  const thankYouPageTranslations = {
    headline: props.data.translations["newsletter_landing_page.thankYou.headline"],
    paragraph: props.data.translations["newsletter_landing_page.thankYou.paragraph"],
    buttonLabel: props.data.translations["newsletter_landing_page.thankYou.buttonLabel"],
  };

  const segmentLabels = {
    birding: props.data.translations["birdingLabel"],
    hunting: props.data.translations["huntingLabel"],
    outdoor: props.data.translations["outdoorLabel"],
  };

  if (!isNewsletterShown) {
    return <Navigate to={linkToHomepage} />;
  }

  return (
    <>
      <PageMetadataComponent
        {...props.data.metadata}
        locationOrigin={locationOrigin}
        canonicalPath={props.page.path}
        languageIsoCode={props.locale.language.isoCode}
      />

      <HeaderComponent header={props.data.header} version={"normal"} />
      <HeaderSpacer />
      <Spacer size={"md"} />

      <NewsletterLandingPageComponent
        currentLanguageIsoCode={props.locale.language.isoCode}
        currentCountryIsoCode={getIsoCodeWithIntFallback(props.locale.country)}
        currentSegment={props.segment.key}
        countries={props.data.countries}
        languages={props.data.languages}
        maybeInitialEmailValue={maybeInitialEmailValue}
        headline={<T>{headline}</T>}
        paragraph={<T>{paragraph}</T>}
        signUpTermsParagraph={<T>{signUpTermsParagraph}</T>}
        segmentSelectorHeadline={<T>{segmentSelectorHeadline}</T>}
        buttonLabel={<T>{buttonLabel}</T>}
        termsAndConditions={termsAndConditions}
        translations={translations}
        generalTranslations={gt}
        otherConditions={otherConditions}
        components={props.data.components}
        segmentLabels={segmentLabels}
        thankYouPageTranslations={thankYouPageTranslations}
        linkToHomepage={linkToHomepage}
        footer={props.data.footer}
        localeForNumberFormat={props.locale}
      />
    </>
  );
};
